import { useMemo } from 'react';
import { useLocation } from 'wouter';
import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { dbg } from '@/lib/dbg';

function getErrorMessage(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    const message = params.get('message');
    if (message) {
      return decodeURIComponent(message);
    }
  } catch (error) {
    dbg.warn('[AUTH_ERROR] Unable to parse error message from URL', error);
  }
  return 'This link is invalid or has expired. Please request a new one and try again.';
}

export default function AuthError(): JSX.Element {
  const [, setLocation] = useLocation();

  const errorMessage = useMemo(() => getErrorMessage(), []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-2xl">We couldn’t verify your link</CardTitle>
          <CardDescription className="text-base text-gray-700">
            {errorMessage}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-gray-600">
          <p>
            This typically happens when an email client prefetches the verification link or when the link has already been used. You can request a fresh link and try again.
          </p>
          <p>
            If the problem persists, please contact support so we can help you get signed in.
          </p>
        </CardContent>
        <CardFooter className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button
            variant="default"
            className="w-full sm:w-auto"
            onClick={() => setLocation('/login')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to login
          </Button>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => setLocation('/forgot-password')}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Request new link
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

